import { supabase, handleSupabaseError } from '@/lib/supabase';
import { Invoice, CreateInvoiceDTO, UpdateInvoiceDTO } from '@/types/models/Invoice';
import { logger } from '@/lib/logger';
import { toNumber } from 'lodash';


export class InvoiceService {
  static async getAllOrdered(storeId: string): Promise<Invoice[]> {
    try {
      logger.info('Fetching all invoices', { storeId });

      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('store_id', storeId)
        .eq('status', 'Ordered')
        .order('creation_time', { ascending: false });

      if (error) {
        logger.error('Failed to fetch invoices', { error, storeId });
        throw error;
      }

      logger.debug('Invoices fetched successfully', {
        storeId,
        count: data?.length
      });

      return data || [];
    } catch (error) {
      logger.error('Error in getAll', { error, storeId });
      throw handleSupabaseError(error);
    }
  }

  static async getAllCompleted(storeId: string): Promise<Invoice[]> {
    try {
      logger.info('Fetching completed invoices', { storeId });

      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('store_id', storeId)
        .eq('status', 'Completed')
        .eq('is_manual_due', false)
        .order('creation_time', { ascending: false });

      if (error) {
        logger.error('Failed to fetch completed invoices', { error, storeId });
        throw error;
      }

      logger.debug('Completed invoices fetched successfully', {
        storeId,
        count: data?.length
      });

      return data || [];
    } catch (error) {
      logger.error('Error in getAllCompleted', { error, storeId });
      throw handleSupabaseError(error);
    }
  }

  static async getById(id: string): Promise<Invoice> {
    try {
      logger.info('Fetching invoice by ID', { id });

      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          line_items:invoice_items(*),
          payment_allocations:payment_allocations(
            *,
            ledger_entry:ledger_entries(*)
          )
        `)
        .eq('id', id)
        .single();

      if (error) {
        logger.error('Failed to fetch invoice', { error, id });
        throw error;
      }

      if (!data) {
        logger.error('Invoice not found', { id });
        throw new Error(`Invoice not found with ID: ${id}`);
      }

      logger.debug('Invoice fetched successfully', { id });
      return data;
    } catch (error) {
      logger.error('Error in getById', { error, id });
      throw handleSupabaseError(error);
    }
  }

  static async create(data: CreateInvoiceDTO): Promise<Invoice> {
    try {
      logger.info('Creating new invoice', { data });

      // Calculate totals
      const totalAmountRaw = data.lineItems.reduce(
        (sum, item) => sum + (toNumber(item.quantity) * toNumber(item.sell_price)),
        0
      );
      const totalAmount = parseFloat(totalAmountRaw.toFixed(2));

      // Always calculate and save both discount amount and percentage with full precision
      let discount: number;
      let discountPercentage: number;
      
      if (data.discountAmount !== undefined && data.discountAmount > 0) {
        // Use exact discount amount provided (preserves full precision)
        discount = data.discountAmount;
        // Calculate percentage from amount with full precision
        discountPercentage = totalAmount > 0 ? (discount / totalAmount) * 100 : 0;
      } else if (data.discountPercentage !== undefined && data.discountPercentage > 0) {
        // Use exact discount percentage provided (preserves full precision)
        discountPercentage = data.discountPercentage;
        // Calculate amount from percentage with full precision
        discount = totalAmount > 0 ? (totalAmount * discountPercentage) / 100 : 0;
      } else {
        discount = 0;
        discountPercentage = 0;
      }

      const billAmountRaw = totalAmount - discount;
      const billAmount = parseFloat(billAmountRaw.toFixed(2));

      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .insert([{
          store_id: data.storeId,
          customer_name: data.customerName,
          customer_name_locale: data.customerNameLocale || data.customerName,
          customer_phone: data.customerPhone,
          payment_method: data.paymentMethod,
          created_by_store_personnel: data.createdByStorePersonnel,
          total_amount: totalAmount,
          discount_percentage: discountPercentage,
          discount: discount, 
          bill_amount: billAmount,
          status: data.status || 'Draft',
          item_count: data.lineItems.length
        }])
        .select()
        .single();

      if (invoiceError) {
        logger.error('Failed to create invoice', { error: invoiceError, data });
        throw invoiceError;
      }

      // Create line items
      const lineItems = data.lineItems.map(({ id, isEditing, ...item }) => ({
        ...item,
        invoice_id: invoice.id,
        quantity: Number(item.quantity),
        sell_price: Number(item.sell_price),
        free_quantity: Number(item.free_quantity || 0),
        serial_number: item.serial_number !== undefined ? item.serial_number : null,
      }));

      const { error: lineItemsError } = await supabase
        .from('invoice_items')
        .insert(lineItems);

      if (lineItemsError) {
        logger.error('Failed to create invoice items', { error: lineItemsError, lineItems });
        throw lineItemsError;
      }

      logger.debug('Invoice created successfully', {
        invoiceId: invoice.id,
        itemCount: lineItems.length
      });



      return invoice;
    } catch (error) {
      logger.error('Error in create', { error, data });
      throw handleSupabaseError(error);
    }
  }

  static async update(invoiceId: string, data: UpdateInvoiceDTO): Promise<Invoice> {
    try {
      logger.info('Updating invoice', { invoiceId, data });

      const updates: any = {};
      if (data.customerName) {
        updates.customer_name = data.customerName;
      }
      if (data.customerPhone) {
        updates.customer_phone = data.customerPhone;
      }
      if (data.status) {
        updates.status = data.status;
      }

      if (data.creationTime) {
        updates.creation_time = data.creationTime;
      }

      if (data.lineItems) {
        const totalAmountRaw = data.lineItems.reduce(
          (sum, item) => sum + (toNumber(item.quantity) * toNumber(item.sell_price)),
          0
        );
        const totalAmount = parseFloat(totalAmountRaw.toFixed(2));

        // Always calculate and save both discount amount and percentage with full precision
        let discount: number;
        let discountPercentage: number;
        
        if (data.discountAmount !== undefined && data.discountAmount > 0) {
          // Use exact discount amount provided (preserves full precision)
          discount = data.discountAmount;
          // Calculate percentage from amount with full precision
          discountPercentage = totalAmount > 0 ? (discount / totalAmount) * 100 : 0;
        } else if (data.discountPercentage !== undefined && data.discountPercentage > 0) {
          // Use exact discount percentage provided (preserves full precision)
          discountPercentage = data.discountPercentage;
          // Calculate amount from percentage with full precision
          discount = totalAmount > 0 ? (totalAmount * discountPercentage) / 100 : 0;
        } else {
          discount = 0;
          discountPercentage = 0;
        }

        const billAmountRaw = totalAmount - discount;
        const billAmount = parseFloat(billAmountRaw.toFixed(2));

        updates.total_amount = totalAmount;
        updates.discount_percentage = discountPercentage; // Store with full precision
        updates.discount = discount; // Store with full precision
        updates.bill_amount = billAmount;
        updates.item_count = data.lineItems.length;

        // Update line items
        const { error: deleteError } = await supabase
          .from('invoice_items')
          .delete()
          .eq('invoice_id', invoiceId);

        if (deleteError) {
          logger.error('Failed to delete existing line items', { error: deleteError, invoiceId });
          throw deleteError;
        }

        const lineItems = data.lineItems.map(({ id, isEditing, ...item }) => ({
          ...item,
          invoice_id: invoiceId,
          quantity: Number(item.quantity),
          sell_price: Number(item.sell_price),
          free_quantity: Number(item.free_quantity || 0),
          serial_number: item.serial_number !== undefined ? item.serial_number : null,
        }));

        const { error: insertError } = await supabase
          .from('invoice_items')
          .insert(lineItems);

        if (insertError) {
          logger.error('Failed to insert updated line items', { error: insertError, lineItems });
          throw insertError;
        }
      }

      if (data.customerNameLocale !== undefined) {
        updates.customer_name_locale = data.customerNameLocale;
      }

      if (data.trackPayment !== undefined) {
        updates.track_payment = data.trackPayment;
      }

      const { data: invoice, error } = await supabase
        .from('invoices')
        .update(updates)
        .eq('id', invoiceId)
        .select(`
          *,
          line_items:invoice_items(*),
          payment_allocations:payment_allocations(*)
        `)
        .single();

      if (error) {
        logger.error('Failed to update invoice', { error, invoiceId, updates });
        throw error;
      }

      logger.debug('Invoice updated successfully', { invoiceId });



      return invoice;
    } catch (error) {
      logger.error('Error in update', { error, invoiceId, data });
      throw handleSupabaseError(error);
    }
  }

  static async delete(id: string): Promise<void> {
    try {
      logger.info('Deleting invoice', { id });

      const { error } = await supabase
        .from('invoices')
        .delete()
        .eq('id', id);

      if (error) {
        logger.error('Failed to delete invoice', { error, id });
        throw error;
      }

      logger.debug('Invoice deleted successfully', { id });
    } catch (error) {
      logger.error('Error in delete', { error, id });
      throw handleSupabaseError(error);
    }
  }

  static async getCustomerInvoices(storeId: string, customerPhone: string) {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('store_id', storeId)
        .eq('customer_phone', customerPhone)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Failed to fetch customer invoices', { error });
      throw error;
    }
  }

  static async getRecentOrders(storeId: string): Promise<Invoice[]> {
    try {
      logger.info('Fetching recent orders', { storeId });

      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          line_items:invoice_items(*)
        `)
        .eq('store_id', storeId)
        .eq('status', 'Draft')
        .order('creation_time', { ascending: false })
        .gt('item_count', 0)
        .limit(3);

      if (error) {
        logger.error('Failed to fetch recent orders', { error, storeId });
        throw error;
      }

      logger.debug('Recent orders fetched successfully', {
        storeId,
        count: data?.length
      });

      return data || [];
    } catch (error) {
      logger.error('Error in getRecentOrders', { error, storeId });
      throw handleSupabaseError(error);
    }
  }

  static async getRecentCompletedInvoices(storeId: string): Promise<Invoice[]> {
    try {
      logger.info('Fetching recent completed invoices', { storeId });

      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          line_items:invoice_items(*)
        `)
        .eq('store_id', storeId)
        .eq('status', 'Completed')
        .eq('is_manual_due', false)
        .order('creation_time', { ascending: false })
        .limit(3);

      if (error) {
        logger.error('Failed to fetch recent completed invoices', { error, storeId });
        throw error;
      }

      logger.debug('Recent completed invoices fetched successfully', {
        storeId,
        count: data?.length
      });

      return data || [];
    } catch (error) {
      logger.error('Error in getRecentCompletedInvoices', { error, storeId });
      throw handleSupabaseError(error);
    }
  }

  static async getAllCompletedAdmin(): Promise<Invoice[]> {
    try {
      logger.info('Fetching all completed invoices (admin)');

      // Step 1: Get total count
      const pageSize = 1000;
      const { count, error: countError } = await supabase
        .from('invoices')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'Completed')
        .eq('is_manual_due', false);

      if (countError) {
        logger.error('Failed to fetch invoice count (admin)', { error: countError });
        throw countError;
      }
      const totalCount = count || 0;
      logger.debug('Total completed invoices (admin):', { totalCount });

      // Step 2: Fetch all pages
      let allInvoices: Invoice[] = [];
      let page = 0;
      while (page * pageSize < totalCount) {
        const from = page * pageSize;
        const to = Math.min((page + 1) * pageSize - 1, totalCount - 1);
        const { data, error } = await supabase
          .from('invoices')
          .select('*')
          .eq('status', 'Completed')
          .eq('is_manual_due', false)
          .order('creation_time', { ascending: false })
          .range(from, to);
        if (error) {
          logger.error('Failed to fetch completed invoices page', { error, page });
          throw error;
        }
        if (data && data.length > 0) {
          allInvoices = allInvoices.concat(data);
        }
        page++;
      }

      logger.debug('All completed invoices fetched successfully (admin)', {
        count: allInvoices.length
      });

      return allInvoices;
    } catch (error) {
      logger.error('Error in getAllCompletedAdmin', { error });
      throw handleSupabaseError(error);
    }
  }

  static async searchCompletedInvoices(
    storeId: string,
    searchQuery: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<{ data: Invoice[]; hasMore: boolean; total: number }> {
    try {
      logger.info('Searching completed invoices', { storeId, searchQuery, page, pageSize });

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      // Use text search for better performance
      const { data, error, count } = await supabase
        .from('invoices')
        .select('*', { count: 'exact' })
        .eq('store_id', storeId)
        .eq('status', 'Completed')
        .eq('is_manual_due', false)
        .or(`customer_name.ilike.%${searchQuery}%,customer_phone.ilike.%${searchQuery}%`)
        .order('creation_time', { ascending: false })
        .range(from, to);

      if (error) {
        logger.error('Failed to search completed invoices', { error, storeId, searchQuery });
        throw error;
      }

      const hasMore = count ? from + pageSize < count : false;

      logger.debug('Completed invoices search successful', {
        storeId,
        searchQuery,
        page,
        pageSize,
        count: data?.length,
        total: count,
        hasMore
      });

      return {
        data: data || [],
        hasMore,
        total: count || 0
      };
    } catch (error) {
      logger.error('Error in searchCompletedInvoices', { error, storeId, searchQuery });
      throw handleSupabaseError(error);
    }
  }

  static async getAllCompletedAdminByDate(date: string): Promise<Invoice[]> {
    try {
      logger.info('Fetching all completed invoices (admin) for date', { date });
      const start = `${date}T00:00:00Z`;
      const end = `${date}T23:59:59.999Z`;
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('status', 'Completed')
        .eq('is_manual_due', false)
        .gte('creation_time', start)
        .lte('creation_time', end)
        .order('creation_time', { ascending: false });
      if (error) {
        logger.error('Failed to fetch completed invoices (admin) for date', { error, date });
        throw error;
      }
      logger.debug('Completed invoices fetched successfully (admin) for date', {
        date,
        count: data?.length
      });
      return data || [];
    } catch (error) {
      logger.error('Error in getAllCompletedAdminByDate', { error, date });
      throw handleSupabaseError(error);
    }
  }

  static async getInvoicesPaginated(
    storeId: string,
    page: number = 1,
    filters: {
      status?: string;
      paymentStatus?: string;
      dateFrom?: string;
      dateTo?: string;
      searchQuery?: string;
    } = {},
    pageSize: number = 20
  ): Promise<{ data: Invoice[]; hasMore: boolean; total: number }> {
    try {
      logger.info('Fetching invoices with filters', { storeId, page, filters, pageSize });
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      let query = supabase
        .from('invoices')
        .select('*', { count: 'exact' })
        .eq('store_id', storeId)
        .eq('is_manual_due', false)
        .in('status', ['Draft', 'Ordered', 'Completed'])
        .gt('item_count', 0);

      // Status filter - only apply if not 'all' and not 'All'
      if (filters.status && filters.status !== 'all' && filters.status !== 'All') {
        query = query.eq('status', filters.status.charAt(0).toUpperCase() + filters.status.slice(1));
      }
      // Payment status filter - only apply if not 'all' and not 'All'
      if (filters.paymentStatus && filters.paymentStatus !== 'all' && filters.paymentStatus !== 'All') {
        if (filters.paymentStatus === 'notTracked') {
          query = query.eq('track_payment', false);
        } else if (filters.paymentStatus === 'paid') {
          query = query.eq('track_payment', true).eq('remaining_dues', 0);
        } else if (filters.paymentStatus === 'notFullyPaid') {
          query = query.eq('track_payment', true).gt('remaining_dues', 0);
        }
      }
      // Date range filter
      if (filters.dateFrom) {
        query = query.gte('creation_time', filters.dateFrom);
      }
      if (filters.dateTo) {
        query = query.lte('creation_time', filters.dateTo);
      }
      // Search
      if (filters.searchQuery && filters.searchQuery.trim()) {
        query = query.or(`customer_name.ilike.%${filters.searchQuery}%,customer_phone.ilike.%${filters.searchQuery}%`);
      }
      query = query.order('creation_time', { ascending: false }).range(from, to);
      const { data, error, count } = await query;
      if (error) {
        logger.error('Failed to fetch invoices with filters', { error, storeId, filters });
        throw error;
      }
      const hasMore = count ? from + pageSize < count : false;
      logger.debug('Invoices fetched successfully with filters', {
        storeId,
        page,
        pageSize,
        count: data?.length,
        total: count,
        hasMore
      });
      return {
        data: data || [],
        hasMore,
        total: count || 0
      };
    } catch (error) {
      logger.error('Error in getInvoicesPaginated', { error, storeId, filters });
      throw handleSupabaseError(error);
    }
  }

  static async getInvoiceStats(storeId: string): Promise<{
    totalInvoices: number;
    newToday: number;
  }> {
    try {
      logger.info('Fetching invoice stats', { storeId });

      // Get total invoices count - use same filters as main query
      const { count: totalCount, error: totalError } = await supabase
        .from('invoices')
        .select('*', { count: 'exact', head: true })
        .eq('store_id', storeId)
        .eq('is_manual_due', false)
        .in('status', ['Draft', 'Ordered', 'Completed'])
        .gt('item_count', 0);

      if (totalError) {
        logger.error('Failed to fetch total invoices count', { error: totalError, storeId });
        throw totalError;
      }

      // Get today's invoices count - use broader date range
      const today = new Date();
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);

      const { count: todayCount, error: todayError } = await supabase
        .from('invoices')
        .select('*', { count: 'exact', head: true })
        .eq('store_id', storeId)
        .eq('is_manual_due', false)
        .in('status', ['Draft', 'Ordered', 'Completed'])
        .gt('item_count', 0)
        .gte('creation_time', todayStart.toISOString())
        .lt('creation_time', todayEnd.toISOString());

      if (todayError) {
        logger.error('Failed to fetch today invoices count', { error: todayError, storeId });
        throw todayError;
      }

      const stats = {
        totalInvoices: totalCount || 0,
        newToday: todayCount || 0
      };

      logger.debug('Invoice stats fetched successfully', { 
        storeId, 
        stats, 
        todayStart: todayStart.toISOString(),
        todayEnd: todayEnd.toISOString(),
        totalCount, 
        todayCount 
      });
      return stats;
    } catch (error) {
      logger.error('Error in getInvoiceStats', { error, storeId });
      throw handleSupabaseError(error);
    }
  }
}